import { MailOutlined } from '@ant-design/icons';
import { Alert, Button, ConfigProvider, Input, Modal, Progress, Space, Table, Tag, Typography, message, theme } from 'antd';
import { useMemo, useRef, useState } from 'react';
import apis from '../../apis';
import { eligibleResultRows, requireSyncedResultRows, matchCommentSheets, readResultPdf, resultRecipientKey,
    validResultDates } from '../../utils/scoringResultEmails';

const { Text, Paragraph } = Typography;
const TEST_EMAIL = 'renaldolouis555@gmail.com';
const blockedStatuses = ['sending', 'uncertain'];
const canSend = recipient => !['sent', 'alreadySent', ...blockedStatuses].includes(recipient.deliveryStatus);

const ScoringResultEmailSender = ({ eventId, category, rows, disabled }) => {
    const [kind, setKind] = useState(null);
    const [recipients, setRecipients] = useState([]);
    const [files, setFiles] = useState([]);
    const [winnerPdf, setWinnerPdf] = useState(null);
    const [dates, setDates] = useState({ confirmationDeadline: '', rundownReleaseDate: '' });
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState('');
    const [progress, setProgress] = useState({ completed: 0, total: 0 });
    const [summary, setSummary] = useState('');
    const requestVersion = useRef(0);
    const folderInput = useRef(null);
    const winnerInput = useRef(null);
    const busy = useRef(false);
    const [confirmationModal, confirmationContext] = Modal.useModal();

    const prepared = useMemo(() => kind === 'nonQualifier' ? matchCommentSheets(recipients, files)
        : recipients.map(recipient => ({ ...recipient, attachment: winnerPdf,
            problem: recipient.problem || (!winnerPdf ? 'Choose the shared guidelines PDF' : '') })),
    [kind, recipients, files, winnerPdf]);
    const pending = prepared.filter(canSend);
    const hasProblems = pending.some(recipient => recipient.problem);
    const ready = !loading && !sending && !testing && !error && pending.length > 0 && !hasProblems
        && (kind !== 'winner' || validResultDates(dates));

    async function openCampaign(nextKind) {
        const version = ++requestVersion.current;
        setKind(nextKind);
        setRecipients([]);
        setFiles([]);
        setWinnerPdf(null);
        setError('');
        setSummary('');
        setProgress({ completed: 0, total: 0 });
        setLoading(true);
        try {
            requireSyncedResultRows(rows);
            const ids = eligibleResultRows(rows, nextKind).map(row => row.registrant.id);
            const preview = [];
            for (let index = 0; index < ids.length; index += 40) {
                const result = await apis.email.previewScoringResults({ eventId, kind: nextKind,
                    registrantIds: ids.slice(index, index + 40) });
                preview.push(...result.recipients);
            }
            if (version === requestVersion.current) setRecipients(preview);
        } catch (err) {
            if (version === requestVersion.current) setError(err.message);
        } finally {
            if (version === requestVersion.current) setLoading(false);
        }
    }

    function closeCampaign() {
        if (sending || testing) return;
        requestVersion.current += 1;
        setKind(null);
    }

    async function sendTest() {
        if (busy.current) return;
        busy.current = true;
        setTesting(true);
        try {
            const attachment = kind === 'winner' && winnerPdf ? await readResultPdf(winnerPdf) : undefined;
            await apis.email.testScoringResults({ kind, dates, attachment });
            message.success(`SMTP accepted the test email to ${TEST_EMAIL}. Check your inbox.`);
        } catch (err) {
            message.error(err.message);
        } finally {
            setTesting(false);
            busy.current = false;
        }
    }

    async function sendCampaign() {
        if (!ready || busy.current) return;
        try {
            requireSyncedResultRows(rows);
        } catch (err) {
            setError(err.message);
            return;
        }
        busy.current = true;
        setSending(true);
        setSummary('');
        setProgress({ completed: 0, total: pending.length });
        const counts = { sent: 0, alreadySent: 0, failed: 0, uncertain: 0 };
        try {
            // Read and validate every chosen file before sending the first email.
            const chosenFiles = new Set(pending.map(recipient => recipient.attachment));
            for (const file of chosenFiles) await readResultPdf(file);
            // Retain only one encoded PDF at a time, even for a large selected folder.
            const sharedAttachment = kind === 'winner' ? await readResultPdf(winnerPdf) : null;
            for (let index = 0; index < pending.length; index += 1) {
                const recipient = pending[index];
                const attachment = sharedAttachment || await readResultPdf(recipient.attachment);
                let result;
                try {
                    result = await apis.email.sendScoringResults({ eventId, kind, dates,
                        registrantId: recipient.registrantId, performerIndex: recipient.performerIndex,
                        snapshot: recipient.snapshot, attachment });
                } catch (err) {
                    // A timeout may occur after SMTP acceptance. Reopen the preview before retrying.
                    result = { status: 'uncertain', message: err.message };
                }
                counts[result.status] = (counts[result.status] || 0) + 1;
                setRecipients(current => current.map(item => resultRecipientKey(item) === resultRecipientKey(recipient)
                    ? { ...item, deliveryStatus: result.status, deliveryMessage: result.message || '' } : item));
                setProgress({ completed: index + 1, total: pending.length });
                if (result.status === 'uncertain') {
                    setError('Sending paused. Reopen the preview and check any uncertain delivery with the SMTP provider before retrying.');
                    break;
                }
                // Stay below the existing 100 requests/minute API limit.
                if (index + 1 < pending.length) await new Promise(resolve => setTimeout(resolve, 1000));
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSummary(`${counts.sent} accepted by SMTP; ${counts.alreadySent} already sent; ${counts.failed} failed; ${counts.uncertain} require review.`);
            setSending(false);
            busy.current = false;
        }
    }

    const columns = [
        { title: 'Performer', dataIndex: 'name' },
        { title: 'Email', dataIndex: 'email' },
        { title: 'Award', dataIndex: 'award' },
        { title: 'PDF', render: (_, recipient) => recipient.attachment?.name || 'Missing' },
        { title: 'Status', render: (_, recipient) => {
            const problem = canSend(recipient) ? recipient.problem : '';
            const status = problem || recipient.deliveryMessage || recipient.deliveryStatus;
            return <Tag color={problem || blockedStatuses.includes(recipient.deliveryStatus) ? 'red'
                : ['sent', 'alreadySent'].includes(recipient.deliveryStatus) ? 'green' : 'gold'}>{status}</Tag>;
        } },
    ];

    return <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: {
        colorPrimary: '#EBBC64', colorBgContainer: '#1E1E1E', colorBgElevated: '#1E1E1E', colorText: '#e5cc92',
    } }}>
        {confirmationContext}
        <Button icon={<MailOutlined />} disabled={disabled || eventId !== 'APCS2026'} onClick={() => openCampaign('winner')}>
            Send Winner Emails
        </Button>
        <Button icon={<MailOutlined />} disabled={disabled || eventId !== 'APCS2026'} onClick={() => openCampaign('nonQualifier')}>
            Send Non-Qualifier Emails
        </Button>
        <Modal open={Boolean(kind)} title={kind === 'winner' ? 'Winner Invitations' : 'Non-Qualifier Results'}
            width={1050} onCancel={closeCampaign} closable={!sending && !testing} maskClosable={false}
            keyboard={!sending && !testing} styles={{ content: { background: '#1E1E1E', color: '#e5cc92' },
                header: { background: '#1E1E1E' } }} footer={<Space wrap>
                <Button disabled={sending || testing} onClick={closeCampaign}>Close</Button>
                <Button loading={testing} disabled={sending || loading} onClick={sendTest}>Send Test Email</Button>
                <Button type="primary" loading={sending} disabled={!ready} onClick={() => confirmationModal.confirm({
                    title: `Send ${pending.length} performer emails?`,
                    content: 'Each performer will receive the listed PDF at their own email address. Review the preview before sending.',
                    okText: 'Send Emails', onOk: sendCampaign,
                    styles: { content: { background: '#1E1E1E', color: '#e5cc92' }, header: { background: '#1E1E1E' } },
                })}>Send {pending.length} Emails</Button>
            </Space>}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Alert type="info" showIcon message={`${eventId} / ${category || 'No category'} — current filtered table, finalized results only`}
                    description={`${recipients.length} performers in this preview. Sync Awards and refresh Scoring Recap after score or penalty edits. Winners receive guidelines only. Tests use Alex Example and go only to ${TEST_EMAIL}.`} />
                {error && <Alert type="error" showIcon message={error} />}
                {summary && <Alert type="info" message={summary} />}
                {kind === 'winner' ? <>
                    <Button disabled={sending || testing} onClick={() => winnerInput.current?.click()}>Choose Shared Guidelines PDF</Button>
                    <input ref={winnerInput} hidden type="file" accept=".pdf" disabled={sending || testing}
                        onChange={event => {
                            const file = event.target.files?.[0];
                            event.target.value = '';
                            if (!file) return;
                            if (file.size > 4 * 1024 * 1024) { message.error('PDF must be at most 4 MB.'); return; }
                            setWinnerPdf(file);
                        }} />
                    {winnerPdf && <Text>{winnerPdf.name}</Text>}
                    <Input aria-label="Confirmation deadline" placeholder="Confirmation deadline, including time zone if applicable"
                        maxLength={160} disabled={sending || testing} value={dates.confirmationDeadline}
                        onChange={event => setDates(current => ({ ...current, confirmationDeadline: event.target.value }))} />
                    <Input aria-label="Rundown release date" placeholder="Rundown release date" maxLength={160}
                        disabled={sending || testing} value={dates.rundownReleaseDate}
                        onChange={event => setDates(current => ({ ...current, rundownReleaseDate: event.target.value }))} />
                    <Paragraph>Required for real invitations. The actual award replaces the winner tier in your template.</Paragraph>
                </> : <>
                    <Button disabled={sending || testing} onClick={() => folderInput.current?.click()}>Choose Comment Sheet Folder</Button>
                    <input ref={folderInput} hidden type="file" webkitdirectory="" directory="" multiple
                        disabled={sending || testing} onChange={event => {
                            setFiles(Array.from(event.target.files || [])); event.target.value = '';
                        }} />
                    <Paragraph>Use one PDF named exactly after each performer, for example “Alex Example.pdf”. Case and surrounding spaces are ignored. Subfolders are included; duplicate names or PDFs block matching. PDFs must be at most 4 MB.</Paragraph>
                    <Text>{files.filter(file => /\.pdf$/i.test(file.name)).length} PDFs selected</Text>
                </>}
                {hasProblems && <Alert type="warning" message="Correct the missing performer details or attachment matches before sending." />}
                {prepared.some(recipient => blockedStatuses.includes(recipient.deliveryStatus))
                    && <Alert type="warning" message="Sending or uncertain deliveries are excluded. Check the SMTP provider before staff repair any uncertain delivery record." />}
                <Table size="small" loading={loading} dataSource={prepared} columns={columns}
                    rowKey={resultRecipientKey} pagination={{ pageSize: 10 }} scroll={{ x: 900 }} />
                {progress.total > 0 && <Progress percent={Math.round(progress.completed / progress.total * 100)}
                    format={() => `${progress.completed}/${progress.total} processed`} />}
            </Space>
        </Modal>
    </ConfigProvider>;
};

export default ScoringResultEmailSender;
